// Turret fire (§31 S8): the volley at the owner's End Turn, overwatch on the
// enemy's moves, and the round's refill. Geometry and appetite are
// `industry/turret.ts`; damage is `damage`, so armor eats it first, wards
// soften it and a last royal is refused (never `attacks.ts` — the Unicorn
// ruling).
//
// **Never inside `makeMove`**: the volley runs in `endTurn`, overwatch in
// `applyMove` after the move (`detonate`'s seat), so a search, perft and the
// pin-aware filter never see a shot. Ascending squares throughout.

import { type Color, fileOf, forwardFacing, KNIGHT, pieceColor, pieceType, rankOf, square } from "./board";
import { areEnemies, pieceAt, type Position } from "./position";
import { damage } from "./reduce";
import { streamFor } from "./random";
import { TURRETS } from "./rules";
import { type TurretShot } from "./tick";
import { targetingArrays } from "./campaign/powers";
import { type IndustryState, structureAt, structureSquares } from "./industry/state";
import {
  canShoot,
  fieldLayers,
  gunOf,
  inField,
  isTurretKind,
  payShot,
  shootable,
  shotsFor
} from "./industry/turret";

/** `owner`'s turrets, by square. */
function turretsOf(industry: IndustryState, owner: Color): number[] {
  return structureSquares(industry).filter((sq) => {
    const one = structureAt(industry, sq)!;
    return one.owner === owner && isTurretKind(one.kind) && one.turret !== undefined;
  });
}

/**
 * A new round for `owner`'s turrets — its turn starting (`endTurn`): shots
 * back to the row's count (scaled by powered arrays), banked fuel expired
 * (§31 M2's rule: points die at their owner's turn start).
 */
export function refreshTurrets(industry: IndustryState, owner: Color): void {
  const arrays = targetingArrays(industry, owner);
  for (const sq of turretsOf(industry, owner)) {
    const one = structureAt(industry, sq)!;
    // `facing` defaulted for a pre-S8 save's turret, which had none.
    one.turret = {
      shots: shotsFor(one.kind, arrays),
      fuel: 0,
      facing: one.turret!.facing ?? forwardFacing(one.owner),
      ...(one.turret!.emplaced ? { emplaced: true } : {})
    };
  }
}

/**
 * The volley (§31 S8): at `owner`'s End Turn every turret of theirs fires
 * at the shootable enemies in its field, **one shot each**, in its order —
 * a Laser at random (`streamFor(seed, ply, TURRETS.laserStream)`), a cone
 * layer by layer, swept. Each shot pays its fuel and ammunition; what is
 * left is overwatch.
 */
export function fireTurrets(position: Position, industry: IndustryState, seed: number, owner: Color): TurretShot[] {
  const shots: TurretShot[] = [];
  const arrays = targetingArrays(industry, owner);
  const roll = streamFor(seed, position.ply, TURRETS.laserStream);
  for (const at of turretsOf(industry, owner)) {
    const one = structureAt(industry, at);
    if (!one) continue;
    const order = fieldLayers(position, at, one, arrays)
      .flat()
      .filter((sq) => shootable(position, owner, sq));
    if (gunOf(one.kind).order === "random") {
      // Fisher–Yates off the seeded stream: a pick without repeats.
      for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(roll() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }
    }
    for (const target of order) {
      if (!canShoot(one)) break;
      // An earlier shot this volley may have killed it, or a blast moved it.
      if (!shootable(position, owner, target)) continue;
      payShot(one);
      shots.push({ from: at, to: target, killed: damage(position, industry, target, gunOf(one.kind).damage) });
    }
  }
  return shots;
}

/**
 * The squares a move's piece passes into (§31 S8 overwatch): a **jump**
 * (a knight, or any move off a straight line) only where it lands — the
 * design's jumps bypass overwatch zones — else every square crossed and the
 * landing.
 */
export function pathInto(position: Position, from: number, to: number): number[] {
  const df = fileOf(to) - fileOf(from);
  const dr = rankOf(to) - rankOf(from);
  const straight = df === 0 || dr === 0 || Math.abs(df) === Math.abs(dr);
  if (!straight || pieceType(pieceAt(position, to)) === KNIGHT) return [to];
  const n = Math.max(Math.abs(df), Math.abs(dr));
  const out: number[] = [];
  for (let i = 1; i <= n; i++) out.push(square(fileOf(from) + (df / n) * i, rankOf(from) + (dr / n) * i));
  return out;
}

/**
 * **Overwatch** (§31 S8): the mover's piece, now on `to`, came from `from`.
 * Every enemy turret with a shot left whose field it **entered** — `from`
 * outside, some square of its path (`pathInto`) inside — fires one shot at
 * it, by square, while it stands. Rest of the round: until the turret's
 * owner's turn starts again.
 */
export function overwatch(position: Position, industry: IndustryState, from: number, to: number): TurretShot[] {
  if (!pieceAt(position, to)) return [];
  const shots: TurretShot[] = [];
  let path: number[] | null = null;
  const arrays = new Map<Color, number>();
  for (const at of structureSquares(industry)) {
    const one = structureAt(industry, at)!;
    if (!isTurretKind(one.kind) || !one.turret) continue;
    if (!pieceAt(position, to)) break;
    if (!areEnemies(position, one.owner, pieceColor(pieceAt(position, to)))) continue;
    if (!canShoot(one) || !shootable(position, one.owner, to)) continue;
    if (!arrays.has(one.owner)) arrays.set(one.owner, targetingArrays(industry, one.owner));
    const count = arrays.get(one.owner)!;
    if (inField(position, at, one, count, from)) continue;
    path ??= pathInto(position, from, to);
    if (!path.some((sq) => inField(position, at, one, count, sq))) continue;
    payShot(one);
    shots.push({ from: at, to, killed: damage(position, industry, to, gunOf(one.kind).damage) });
  }
  return shots;
}
